/**
 * Kadmin Dark Theme
 *
 * Reuses all light theme structure/functions/typography/borders,
 * but swaps in dark colors.
 */
import { createTheme } from "@mui/material/styles";

// Reuse light theme base (except colors) and functions
import breakpoints from "assets/theme/base/breakpoints";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";

import boxShadow from "assets/theme/functions/boxShadow";
import hexToRgb from "assets/theme/functions/hexToRgb";
import linearGradient from "assets/theme/functions/linearGradient";
import pxToRem from "assets/theme/functions/pxToRem";
import rgba from "assets/theme/functions/rgba";

// Dark-specific
import colors from "assets/theme-dark/base/colors";

const { grey, primary, text, background } = colors;

const boxShadows = {
  xs: "0 1px 2px 0 rgba(0, 0, 0, 0.2)",
  sm: "0 1px 3px 0 rgba(0, 0, 0, 0.3)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.2)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -4px rgba(0, 0, 0, 0.2)",
  xl: "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
  xxl: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  navbarBoxShadow: "none",
  tabsBoxShadow: { indicator: "none" },
  sliderBoxShadow: { thumb: "0 1px 3px 0 rgba(0, 0, 0, 0.4)" },
};

const globals = {
  html: { scrollBehavior: "smooth" },
  "*, *::before, *::after": { margin: 0, padding: 0 },
  "a, a:link, a:visited": { textDecoration: "none !important" },
  "a.link, .link": { color: `${text.main} !important`, transition: "color 150ms ease-in !important" },
  "a.link:hover, .link:hover": { color: `${primary.main} !important` },
};

const container = {
  ".MuiContainer-root": {
    paddingRight: `${pxToRem(24)} !important`,
    paddingLeft: `${pxToRem(24)} !important`,
  },
};

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: { boxShadow, hexToRgb, linearGradient, pxToRem, rgba },

  components: {
    MuiCssBaseline: { styleOverrides: { ...globals, ...container } },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          width: pxToRem(250),
          backgroundColor: background.sidenav || background.paper,
          borderRight: `1px solid ${grey[200]}`,
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: background.card || background.paper,
          border: `1px solid ${grey[200]}`,
          borderRadius: borders.borderRadius.xl,
          boxShadow: boxShadows.sm,
          color: text.main,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", borderRadius: borders.borderRadius.md },
        contained: { backgroundColor: primary.main, "&:hover": { backgroundColor: primary.dark } },
        outlined: { borderColor: grey[300], color: text.main, "&:hover": { borderColor: primary.main } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": { borderColor: grey[300] },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: grey[400] },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primary.main },
          color: text.main,
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: text.secondary } } },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: background.paper, border: `1px solid ${grey[200]}` } } },
    MuiMenuItem: { styleOverrides: { root: { color: text.main, "&:hover": { backgroundColor: grey[100] } } } },
    MuiDivider: { styleOverrides: { root: { backgroundColor: grey[200] } } },
    MuiTableCell: { styleOverrides: { root: { borderBottom: `1px solid ${grey[200]}`, color: text.main } } },
    MuiTableHead: { styleOverrides: { root: { "& .MuiTableRow-root th": { backgroundColor: grey[100], color: text.secondary } } } },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: primary.main } } },
    MuiTab: { styleOverrides: { root: { color: text.secondary, "&.Mui-selected": { color: primary.main }, textTransform: "none" } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: grey[200], color: text.main } } },
    MuiDialog: { styleOverrides: { paper: { backgroundColor: background.paper, borderRadius: borders.borderRadius.xl } } },
    MuiAvatar: { styleOverrides: { root: { fontSize: pxToRem(14), fontWeight: 600 } } },
    MuiAppBar: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiPopover: { styleOverrides: { paper: { backgroundColor: background.paper, border: `1px solid ${grey[200]}` } } },
    MuiLinearProgress: { styleOverrides: { root: { backgroundColor: grey[200] } } },
    MuiCheckbox: { styleOverrides: { root: { color: grey[400], "&.Mui-checked": { color: primary.main } } } },
    MuiRadio: { styleOverrides: { root: { color: grey[400], "&.Mui-checked": { color: primary.main } } } },
    MuiSwitch: { styleOverrides: { switchBase: { "&.Mui-checked": { color: primary.main } } } },
    MuiSlider: { styleOverrides: { root: { "& .MuiSlider-thumb": { boxShadow: boxShadows.sliderBoxShadow.thumb } } } },
  },
});
